import {
  closestCenter,
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { TableRow } from '@ui/components';
import { cn } from '@ui/lib/utils';
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useAppTableContext } from '../../lib/context/use-app-table-context';
import { useSkeletonRowCount } from '../../lib/hooks/use-skeleton-row-count';
import { useVirtualTable } from '../../lib/hooks/use-virtual-table';
import { AppTableNewRef } from '../../types/table-props';
import { AppTableCell } from '../app-table-cell';
import { AppTableDragHeaderFilter, AppTableHeader } from '../app-table-header';
import { AppTableSkeleton } from '../app-table-skeleton';
import { AppTableToolbar } from '../app-table-toolbar';
import { TableCard } from '../table-card';

const TableComponent = <TData,>(
  _: never,
  ref: ForwardedRef<AppTableNewRef<TData>>,
) => {
  const {
    className,
    rowClassName,
    calculatedRowClassName,
    calculatedRowStyle,
    data,
    rowSelection,
    isSkeleton,
    isDraggable,
    columns,
    setRowSelection,
    getRowId,
    onClickRow,
    onDoubleClickRow,
    enableColumnFilter,
    rowHeight = 38,
    customSelectColumn,
    columnPinning,
    isTemplate,
    defaultSorting,
  } = useAppTableContext<TData>();

  const {
    table,
    rows,
    tableContainerRef,
    rowVirtualizer,
    columnOrder,
    setColumnOrder,
  } = useVirtualTable({
    data,
    columns,
    rowSelection,
    setRowSelection,
    getRowId,
    rowHeight,
    customSelectColumn,
    columnPinning,
    isTemplate,
    defaultSorting,
  });

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
  }, [table.getState().columnSizingInfo, table.getState().columnSizing]);

  const skeletonColumns = table.getVisibleLeafColumns().map((column) => ({
    id: column.id,
    width: `calc(var(--col-${column.id}-size) * 1px)`,
  }));
  const tableHeaderRef = useRef<HTMLTableSectionElement>(null);
  const skeletonRowCount = useSkeletonRowCount({
    containerRef: tableContainerRef,
    headerRef: tableHeaderRef,
    rowHeight,
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((columnOrder) => {
        const oldIndex = columnOrder.indexOf(active.id as string);
        const newIndex = columnOrder.indexOf(over.id as string);
        return arrayMove(columnOrder, oldIndex, newIndex);
      });
    }
  }, []);

  useImperativeHandle(ref, () => ({
    getTable: () => table,
  }));

  return (
    <TableCard className={className}>
      <AppTableToolbar table={table} />
      <DndContext
        autoScroll={false}
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        sensors={sensors}
        onDragEnd={handleDragEnd}
      >
        <div
          ref={tableContainerRef}
          className={cn(
            'relative min-h-0 flex-1 overflow-auto pb-4 [scrollbar-gutter:stable_both-edges]',
          )}
        >
          <table
            className="grid w-full"
            style={{
              display: 'grid',
              width:
                table.getTotalSize() < document.body.offsetWidth
                  ? undefined
                  : table.getTotalSize(),
              ...columnSizeVars,
            }}
          >
            <thead
              ref={tableHeaderRef}
              className="sticky top-0 z-10 grid w-full border-t"
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  className="flex w-full bg-white"
                >
                  <SortableContext
                    disabled={!isDraggable}
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header) => (
                      <AppTableHeader
                        key={header.id}
                        className="flex"
                        header={header}
                        style={{
                          width: `calc(var(--col-${header.column.id}-size) * 1px)`,
                        }}
                      />
                    ))}
                  </SortableContext>
                </TableRow>
              ))}
              {enableColumnFilter &&
                table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="flex w-full bg-white"
                  >
                    <SortableContext
                      disabled={!isDraggable}
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      {headerGroup.headers.map((header) => (
                        <AppTableDragHeaderFilter
                          key={header.id}
                          header={header}
                          style={{
                            width: `calc(var(--col-${header.column.id}-size) * 1px)`,
                          }}
                        />
                      ))}
                    </SortableContext>
                  </TableRow>
                ))}
            </thead>
            <tbody
              className="relative w-full"
              style={{
                height: isSkeleton
                  ? `${skeletonRowCount * rowHeight}px`
                  : `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
              }}
            >
              {isSkeleton ? (
                <AppTableSkeleton
                  cellClassName="flex"
                  columns={skeletonColumns}
                  rowClassName="absolute flex w-full"
                  rowCount={skeletonRowCount}
                  rowHeight={rowHeight}
                  getRowStyle={(rowIndex) => ({
                    transform: `translateY(${rowIndex * rowHeight}px)`,
                    width: '100%',
                  })}
                />
              ) : (
                rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const row = rows[virtualRow.index];
                  return (
                    <TableRow
                      key={row.id}
                      className={cn(
                        'absolute flex w-full',
                        rowClassName,
                        calculatedRowClassName?.(row.original, row),
                        onClickRow || onDoubleClickRow
                          ? 'cursor-pointer'
                          : 'cursor-default',
                      )}
                      data-state={row.getIsSelected() && 'selected'}
                      data-index={virtualRow.index}
                      style={{
                        transform: `translateY(${virtualRow.start}px)`,
                        width: '100%',
                        height: rowHeight,
                        ...calculatedRowStyle?.(row.original, row),
                      }}
                    >
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <AppTableCell
                            key={cell.id}
                            cell={cell}
                            className="flex truncate text-nowrap"
                            style={{
                              width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
                            }}
                          />
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </DndContext>
    </TableCard>
  );
};

export const VirtualTable = forwardRef(TableComponent) as <TData>(
  props: never & {
    ref?: ForwardedRef<AppTableNewRef<TData>>;
  },
) => ReturnType<typeof TableComponent>;
